<template>
    <div class="historic-content">
        <SectionContainer
            title="Histórico da Reserva"
            icon="history"
        >
            <div v-if="history.length > 0" class="timeline-container">
                <!-- Timeline de histórico -->
                <div class="timeline">
                    <div
                        v-for="(item, index) in history"
                        :key="item.id"
                        class="timeline-item"
                        :class="getTimelineItemClass(item.reserve_status)"
                    >
                        <div class="timeline-marker">
                            <i :class="getStatusIcon(item.reserve_status)"></i>
                        </div>

                        <div class="timeline-content">
                            <div class="timeline-header">
                                <div class="status-info">
                                    <span
                                        class="status-badge"
                                        :class="getStatusClass(item.reserve_status)"
                                    >
                                        {{ item.reserve_status ? item.reserve_status.title : 'Status não informado' }}
                                    </span>
                                    <span class="timeline-date">
                                        <i class="fas fa-clock"></i>
                                        {{ item.created_at }}
                                    </span>
                                </div>
                            </div>

                            <div class="timeline-body" v-if="item.observation">
                                <div class="observation-container">
                                    <div class="observation-label">
                                        <i class="fas fa-comment-alt"></i>
                                        Observação:
                                    </div>
                                    <div class="observation-text">
                                        {{ item.observation }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Estado vazio -->
            <div v-else-if="!isLoading" class="empty-state">
                <div class="empty-icon">
                    <i class="fas fa-history"></i>
                </div>
                <div class="empty-title">Nenhum histórico encontrado</div>
                <div class="empty-subtitle">
                    Esta reserva ainda não possui registros de alterações.
                </div>
            </div>

            <!-- Estado de carregamento -->
            <div v-else class="loading-state">
                <div class="loading-spinner">
                    <i class="fas fa-spinner fa-spin"></i>
                </div>
                <div class="loading-text">Carregando histórico...</div>
            </div>
        </SectionContainer>
    </div>
</template>

<script>
import SectionContainer from "../../Modals/Checklist/SectionContainer.vue";

export default {
    components: {
        SectionContainer
    },
    data() {
        return {
            history: [], // history - array com histórico da reserva
            isLoading: false, // isLoading - estado de carregamento
            errors: null, // errors - erros da API
            showTableView: false // showTableView - alterna entre timeline e tabela
        };
    },
    methods: {
        getHistory() { // getHistory - busca histórico da reserva na API
            let self = this;
            let id = self.$route.params.id;
            self.isLoading = true;

            let api = self.$store.state.api + "reserveVehiclesHistory/reserve-id/" + id;

            self.$http
                .get(api)
                .then((response) => {
                    self.history = response.data;
                })
                .catch((error) => {
                    self.errors = error.response.data;
                    self.$message(null, self.errors, "error");
                })
                .finally(() => {
                    self.isLoading = false;
                });
        },

        getStatusClass(status) { // getStatusClass - retorna classe CSS baseada no status
            if (!status) return 'status-unknown';

            const statusClasses = {
                1: 'status-pending', // Pré-reserva
                2: 'status-approved', // Reservado
                3: 'status-canceled', // Cancelado
                4: 'status-progress', // Retirado
                5: 'status-completed' // Devolvido
            };
            return statusClasses[status.id] || 'status-unknown';
        },
        getStatusIcon(status) { // getStatusIcon - retorna ícone baseado no status
            if (!status) return 'fas fa-question-circle';

            const statusIcons = {
                1: 'fas fa-clock', // Pré-reserva
                2: 'fas fa-check-circle', // Reservado
                3: 'fas fa-times-circle', // Cancelado
                4: 'fas fa-car', // Retirado
                5: 'fas fa-flag-checkered' // Devolvido
            };
            return statusIcons[status.id] || 'fas fa-circle';
        },
        getTimelineItemClass(status) { // getTimelineItemClass - classe para item da timeline
            if (!status) return 'timeline-unknown';

            const timelineClasses = {
                1: 'timeline-pending',
                2: 'timeline-approved',
                3: 'timeline-canceled',
                4: 'timeline-progress',
                5: 'timeline-completed'
            };
            return timelineClasses[status.id] || 'timeline-unknown';
        }
    },
    mounted() {
        this.getHistory();
    }
};
</script>

<style scoped>
.historic-content {
    padding: 20px;
    background-color: white;
}

/* Timeline */
.timeline-container {
    padding: 20px 0;
}

.timeline {
    position: relative;
    padding-left: 30px;
}

.timeline::before {
    content: '';
    position: absolute;
    left: 15px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: linear-gradient(to bottom, #e2e8f0, #cbd5e0);
}

.timeline-item {
    position: relative;
    margin-bottom: 30px;
    padding-left: 40px;
}

.timeline-item:last-child {
    margin-bottom: 0;
}

.timeline-marker {
    position: absolute;
    left: -40px;
    top: 5px;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background-color: white;
    border: 3px solid #e2e8f0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
    transition: all 0.3s ease;
}

.timeline-marker i {
    font-size: 12px;
    color: #6c757d;
}

/* Classes específicas para cada tipo de timeline */
.timeline-pending .timeline-marker {
    border-color: #8B00C7;
    background-color: #8B00C7;
}

.timeline-pending .timeline-marker i {
    color: white;
}

.timeline-approved .timeline-marker {
    border-color: #FE8D18;
    background-color: #FE8D18;
}

.timeline-approved .timeline-marker i {
    color: white;
}

.timeline-canceled .timeline-marker {
    border-color: #DC3545;
    background-color: #DC3545;
}

.timeline-canceled .timeline-marker i {
    color: white;
}

.timeline-progress .timeline-marker {
    border-color: #FFA500;
    background-color: #FFA500;
}

.timeline-progress .timeline-marker i {
    color: white;
}

.timeline-completed .timeline-marker {
    border-color: #000000;
    background-color: #000000;
}

.timeline-completed .timeline-marker i {
    color: white;
}

.timeline-content {
    background-color: #f8f9fa;
    border-radius: 8px;
    padding: 16px;
    border-left: 4px solid #e2e8f0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    transition: all 0.3s ease;
}

.timeline-content:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.timeline-pending .timeline-content {
    border-left-color: #8B00C7;
}

.timeline-approved .timeline-content {
    border-left-color: #FE8D18;
}

.timeline-canceled .timeline-content {
    border-left-color: #DC3545;
}

.timeline-progress .timeline-content {
    border-left-color: #FFA500;
}

.timeline-completed .timeline-content {
    border-left-color: #000000;
}

.timeline-header {
    margin-bottom: 12px;
}

.status-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
}

.timeline-date {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 13px;
    color: #6c757d;
    font-weight: 500;
}

.timeline-date i {
    color: #f8981d;
}

/* Status badges */
.status-badge {
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.status-badge-small {
    padding: 3px 8px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
}

.status-pending {
    background-color: #8B00C7;
    color: #ffffff;
}

.status-approved {
    background-color: #FE8D18;
    color: #ffffff;
}

.status-canceled {
    background-color: #DC3545;
    color: #ffffff;
}

.status-progress {
    background-color: #FFA500;
    color: #ffffff;
}

.status-completed {
    background-color: #000000;
    color: #ffffff;
}

.status-unknown {
    background-color: #6c757d;
    color: #ffffff;
}

/* Observação */
.observation-container {
    background-color: white;
    border-radius: 6px;
    padding: 12px;
    border: 1px solid #e2e8f0;
}

.observation-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
    color: #495057;
    margin-bottom: 6px;
    font-size: 13px;
}

.observation-label i {
    color: #f8981d;
}

.observation-text {
    color: #2c3e50;
    line-height: 1.5;
    font-size: 14px;
}

/* Estados vazios e carregamento */
.empty-state, .loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;
}

.empty-icon, .loading-spinner {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: linear-gradient(135deg, #f8f9fa, #e9ecef);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
    font-size: 2rem;
    color: #6c757d;
}

.loading-spinner i {
    color: #f8981d;
}

.empty-title, .loading-text {
    font-size: 1.25rem;
    font-weight: 600;
    color: #495057;
    margin-bottom: 8px;
}

.empty-subtitle {
    color: #6c757d;
    font-size: 1rem;
}

/* Visualização da tabela */
.view-toggle {
    margin-bottom: 20px;
    text-align: right;
}

.table-responsive {
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.table {
    margin-bottom: 0;
}

.thead-light th {
    background-color: #f8f9fa;
    border-bottom: 2px solid #e2e8f0;
    font-weight: 600;
    color: #495057;
    text-transform: uppercase;
    font-size: 12px;
    letter-spacing: 0.5px;
}

.date-cell {
    display: flex;
    align-items: center;
    font-size: 13px;
    color: #495057;
}

.observation-cell {
    font-size: 14px;
    color: #2c3e50;
    line-height: 1.4;
    max-width: 300px;
    word-wrap: break-word;
}

/* Responsividade */
@media (max-width: 768px) {
    .historic-content {
        padding: 15px;
    }

    .timeline {
        padding-left: 20px;
    }

    .timeline::before {
        left: 10px;
    }

    .timeline-item {
        padding-left: 30px;
    }

    .timeline-marker {
        left: -25px;
        width: 20px;
        height: 20px;
    }

    .timeline-marker i {
        font-size: 10px;
    }

    .timeline-content {
        padding: 12px;
    }

    .status-info {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }

    .view-toggle {
        text-align: center;
    }
}

@media (max-width: 576px) {
    .timeline-date {
        font-size: 12px;
    }

    .observation-text {
        font-size: 13px;
    }

    .empty-icon, .loading-spinner {
        width: 60px;
        height: 60px;
        font-size: 1.5rem;
    }

    .empty-title, .loading-text {
        font-size: 1.1rem;
    }

    .table {
        font-size: 13px;
    }

    .observation-cell {
        max-width: 200px;
        font-size: 13px;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/ReserveVehicles/view-components/ViewHistoricReserve.vue